import styled from 'styled-components'

import { Skeleton } from '@blade/ui-kit'

import type { TransactionListItemVariant } from './transaction-list-item'
import type { ClassNameProps } from '@root/shared/react'

type TransactionListItemSkeletonProps = {
  withWallet?: boolean
  variant?: TransactionListItemVariant
} & ClassNameProps

type VariantProps = {
  variant: TransactionListItemVariant
}

export const TransactionListItemSkeleton: React.FC<
  TransactionListItemSkeletonProps
> = ({ className, withWallet = false, variant = 'row' }) => {
  if (variant === 'square') {
    return (
      <Root className={className} data-variant="square">
        <Header>
          <TypeRoot data-variant="square">
            <Skeleton width={10} height={10} variant="circular" />
            <Skeleton width={34} height={12} paddingBlock={2} />
          </TypeRoot>
          <WalletSkeleton variant={variant} />
        </Header>
        <PricesSkeleton variant="square" />
        <StatusSkeleton variant="square" />
      </Root>
    )
  }

  return (
    <Root className={className}>
      {withWallet ? (
        <PricesGroup>
          <WalletGroup>
            <WalletSkeleton variant={variant} />
            <TypeRoot data-variant={variant}>
              <Skeleton width={10} height={10} variant="circular" />
              <Skeleton width={34} height={12} paddingBlock={2} />
            </TypeRoot>
          </WalletGroup>
          <PricesSkeleton variant={variant} />
        </PricesGroup>
      ) : (
        <PricesSkeleton variant={variant} />
      )}
      <StatusSkeleton variant={variant} />
    </Root>
  )
}

const WalletSkeleton: React.FC<VariantProps> = ({ variant }) => (
  <Wallet data-variant={variant}>
    <Skeleton width={10} height={10} variant="circular" />
    <Skeleton width={80} height={14} paddingBlock={3} />
  </Wallet>
)

const PricesSkeleton: React.FC<VariantProps> = ({ variant }) => (
  <Prices data-variant={variant}>
    <AmountSkeleton />
    {variant === 'row' ? (
      <TxTypeIcon width={13} height={13} variant="circular" />
    ) : undefined}
    <AmountEnd />
  </Prices>
)

const AmountSkeleton: React.FC<ClassNameProps> = ({ className }) => (
  <Amount className={className}>
    <Skeleton width={26} height={26} variant="circular" />
    <AmountGroup>
      <Skeleton width={64} height={12} paddingBlock={2} />
      <Skeleton width={36} height={12} paddingBlock={3} />
    </AmountGroup>
  </Amount>
)

const StatusSkeleton: React.FC<VariantProps> = ({ variant }) => (
  <Status data-variant={variant}>
    <Skeleton width={74} height={10} paddingBlock={2} />
    <State>
      <Skeleton width={9} height={9} variant="circular" />
      <Skeleton width={34} height={10} paddingBlock={2} />
    </State>
  </Status>
)

const Root = styled.div`
  --tx-background-color: var(--appearanceA-5);

  background: var(--tx-background-color);

  border-radius: 8px;
  align-items: center;
  overflow: hidden;
  position: relative;

  display: grid;
  gap: 8px;
  padding: 10px 8px;

  &[data-variant='square'] {
    gap: 6px;
  }
`

const WalletGroup = styled.div`
  display: grid;
  grid-template-rows: 1fr auto;
  align-content: start;
  align-items: start;
`

const PricesGroup = styled.div`
  display: grid;
  grid-template-columns: 0.5fr 1fr;
`

const Header = styled.div`
  margin-bottom: 2px;
`

const TypeRoot = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: auto auto;
  justify-content: start;
  gap: 2px;

  &[data-variant='square'] {
    gap: 2px;
  }
`

const Wallet = styled.div`
  display: grid;
  align-items: center;
  grid-auto-flow: column;
  justify-content: start;
  gap: 4px;

  &[data-variant='square'] {
    gap: 2px;
  }
`

const Prices = styled.div`
  display: grid;

  &[data-variant='row'] {
    grid-template-columns: minmax(0, 1fr) auto minmax(0, 1fr);
    gap: 5px;
  }

  &[data-variant='square'] {
    gap: 4px;
    justify-content: start;
  }
`

const Amount = styled.div`
  display: grid;
  gap: 5px;
  grid-auto-flow: column;
  align-items: start;
  justify-content: start;

  > div:first-child {
    margin-top: 1px;
  }
`

const AmountEnd = styled(AmountSkeleton)`
  justify-self: end;

  ${Prices}[data-variant='square'] & {
    justify-self: start;
  }
`

const AmountGroup = styled.div`
  display: grid;
`

const TxTypeIcon = styled(Skeleton)`
  place-self: center;
`

const Status = styled.div`
  display: grid;
  justify-content: space-between;
  grid-template-columns: auto auto;
  align-items: center;

  &[data-variant='square'] {
    border-top: 1px solid var(--background-3);
    padding-top: 4px;
  }
`

const State = styled.div`
  display: grid;
  gap: 2px;
  grid-template-columns: auto auto;
  align-items: center;
  justify-items: end;
`
